import { RootState } from 'store/types';

import { createSelector } from 'reselect';
import { TemporaryTermsAndConditions } from 'store/global/reducers';
import { searchFormattedClinicNotes } from 'store/clinicNotes/selectors';
import { searchFormattedMedications } from 'store/medications/selectors';
import { searchFormattedTestResults } from 'store/testResults/selectors.common';
import { searchFormattedVisitSummaries } from 'store/visitSummary/selectors';
import { searchFormattedProcedureHistory } from 'store/medicalHistory/procedures/selectors';
import { searchFormattedAllergies } from 'store/medicalHistory/allergies/selectors';
import { searchFormattedSocialHistory } from 'store/medicalHistory/socialHistory/selectors';
import { searchFormattedImmunizations } from 'store/medicalHistory/immunizations/selectors';
import { searchFormattedProblems } from 'store/medicalHistory/problems/selectors';
import { searchFormattedVitals } from 'store/vitals/selectors';
import { searchFormattedInbox, searchFormattedOutbox } from 'store/messaging/selectors';
import { searchFormattedMyDoctors } from 'store/myDoctors/selectors';
import { searchFormattedAppointments } from 'store/booking/selectors';
import { searchFormattedHelp } from 'lib/constants/help';
import { searchFormattedAccount } from 'store/account/selectors';
import { searchFormattedBilling } from 'store/billing/selectors';

import { getCareLinks } from 'store/getCare/getCareConstants';

export const globalStateSelector = (state: RootState) => state.global;

export const termsAndConditionsSelector = createSelector(
  globalStateSelector,
  state => state.termsAndConditions
);

export const providerSearchTermsSelector = createSelector(
  termsAndConditionsSelector,
  terms => terms?.provider
);

export const tempTermsAndConditionsSelector = createSelector(
  globalStateSelector,
  state => state.temporaryTCAccept || ({} as TemporaryTermsAndConditions)
);

export const initialNavigationCompleteSelector = createSelector(
  globalStateSelector,
  state => state.initialNavigationComplete
);

export const navigationPathAfterAuthenticationSelector = createSelector(
  globalStateSelector,
  global => global.navigationPathAfterAuthentication
);

export const navigationParamAfterAuthenticationSelector = createSelector(
  globalStateSelector,
  global => global.navigationParamAfterAuthentication
);

export const hasFetchedDependencies = createSelector(
  globalStateSelector,
  global => global.hasFetchedDependencies
);

export const isFetchingDependenciesSelector = createSelector(
  globalStateSelector,
  global => global.isFetchingDependencies
);

export const globalIsFetchingSelector = createSelector(
  globalStateSelector,
  global => global.isFetching
);

export const globalErrorSelector = createSelector(globalStateSelector, global => global.error);

export const mhpStatusSelector = createSelector(globalStateSelector, global => global.mhpStatus);
export const mhpStatusIsFetchingSelector = createSelector(
  mhpStatusSelector,
  mhpStatus => mhpStatus.isFetching
);
export const mhpStatusErrorSelector = createSelector(
  mhpStatusSelector,
  mhpStatus => mhpStatus.error
);
export const mhpStatusDataSelector = createSelector(mhpStatusSelector, mhpStatus => mhpStatus.data);

export const loginTypeSelector = createSelector(globalStateSelector, global => global.loginType);

// TODO REMOVE FAKE DATA
export const FakeStaticData = [
  {
    // medication
    title: 'Antifungal Foot Cream',
    provider: 'Dr. Jack Kevorkian',
    location: 'Draper',
    date: 'August 7, 2022',
    category: 'Medications',
    icon: 'PillBottle',
    keywords: ['fungus', 'antifungal', 'prescriptions'],
    navWebPath: `/u/health-record/`
  },
  {
    // medication
    title: 'Antifungal Pain Reliever',
    provider: 'Dr. Jekyll',
    location: 'Draper',
    date: 'August 7, 2022',
    category: 'Medications',
    icon: 'PillBottle',
    keywords: ['fungus', 'antifungal', 'prescriptions'],
    navWebPath: `/u/health-record/`
  },
  {
    // medication
    title: 'Corticosteroid Nasal Spray - cat allergy treatment',
    provider: 'Dr. Miranda Bailey',
    location: 'Draper',
    date: 'August 7, 2022',
    category: 'Medications',
    icon: 'PillBottle',
    keywords: ['cat', 'cat allergy', 'allergy'],
    navWebPath: `/u/health-record/`
  },
  {
    // medication
    title: 'Tricyclic antidepressants - chronic cat anxiety',
    provider: 'Dr. John Dorian',
    location: 'Draper',
    date: 'August 7, 2022',
    category: 'Medications',
    icon: 'PillBottle',
    keywords: ['cat', 'cat allergy', 'allergy'],
    navWebPath: `/u/health-record/`
  },
  {
    // vaccine
    title: `Immunization - Cat Vaccination`,
    date: 'July 18, 2022',
    keywords: ['cat', 'cat allergy', 'allergy', 'cat vaccine'],
    navWebPath: '/u/health-record/immunizations',
    navParams: {},
    icon: 'MedicalHistoryIcon',
    category: 'Immunizations'
  },
  {
    // allergy
    title: `Allergy - Cat Allergy`,
    date: 'August 9, 2022',
    keywords: ['cat', 'cat allergy', 'allergy'],
    navWebPath: '/u/health-record/allergies',
    navParams: {},
    icon: 'MedicalHistoryIcon',
    weight: 1,
    category: 'Allergies'
  },
  {
    // doctor
    title: 'Dr. DooLittle',
    provider: 'Catoligist- Aids in all cat needs',
    location: 'Salt Lake City',
    date: '',
    category: 'myDoctors',
    icon: 'MyDoctors',
    keywords: ['doctor', 'cat allergy', 'cats', 'cat'],
    navWebPath: `/u/get-care-now`
  },
  {
    // doctor
    title: 'Dr. Jekyll',
    provider: 'Foot removal - Removing feet since 1886',
    location: 'Draper',
    date: '',
    category: 'myDoctors',
    icon: 'MyDoctors',
    keywords: ['fungus', 'antifungal', 'prescriptions'],
    navWebPath: `/u/get-care-now`
  },
  {
    // doctor
    title: 'Dr. Shepard McDreamy',
    provider: 'Foot Guy - fungal foot specialist',
    location: 'Salt Lake City',
    date: '',
    category: 'myDoctors',
    icon: 'MyDoctors',
    keywords: ['fungus', 'antifungal', 'prescriptions'],
    navWebPath: `/u/get-care-now`
  },
  {
    // doctor
    title: 'Dr Doofenshmirtz',
    provider: 'Cat Allergy Specialist',
    location: 'Provo',
    date: '',
    category: 'myDoctors',
    icon: 'MyDoctors',
    keywords: ['doctor', 'cat allergy', 'cats', 'cat'],
    navWebPath: `/u/get-care-now`
  },
  {
    // doctor
    title: 'Dr Stephen Strange',
    provider: 'Macaroni Specialist',
    location: 'Orem',
    date: '',
    category: 'myDoctors',
    icon: 'MyDoctors',
    keywords: ['art', 'macaroni', 'therapy'],
    navWebPath: `/u/get-care-now`
  },
  {
    // appointments
    title: 'Cat Removal Appointment',
    provider: 'Dr. John Dorian',
    location: 'Your Home',
    date: 'Tomorrow',
    category: 'appointments',
    icon: 'CalendarFlat',
    keywords: ['doctor', 'cat allergy', 'cats', 'cat'],
    navWebPath: `/u/get-care-now`
  },
  {
    // appointments
    title: 'Foot Fungus Discussion',
    provider: 'Dr. Shepard McDreamy',
    location: 'Your Home',
    date: 'August 10, 2022',
    category: 'appointments',
    icon: 'CalendarFlat',
    keywords: ['fungus', 'antifungal', 'prescriptions'],
    navWebPath: `/u/get-care-now`
  },
  {
    // appointments
    title: 'Macaroni Art Therapy',
    provider: 'Dr Stephen Strange',
    location: 'Your Home',
    date: 'August 15, 2022',
    category: 'appointments',
    icon: 'CalendarFlat',
    keywords: ['art', 'macaroni', 'therapy'],
    navWebPath: `/u/get-care-now`
  }
];

export const globalSearchItems = createSelector(
  [
    searchFormattedClinicNotes,
    searchFormattedMedications,
    searchFormattedTestResults,
    searchFormattedVisitSummaries,
    searchFormattedProcedureHistory,
    searchFormattedAllergies,
    searchFormattedSocialHistory,
    searchFormattedImmunizations,
    searchFormattedProblems,
    searchFormattedVitals,
    searchFormattedInbox,
    searchFormattedOutbox,
    searchFormattedMyDoctors,
    searchFormattedAppointments,
    getCareLinks
  ],
  (
    notes,
    medications,
    testResults,
    visitSummaries,
    procedures,
    allergies,
    socialHistory,
    immunizations,
    problems,
    vitals,
    inbox,
    outbox,
    myDoctors,
    appointments,
    getCare
  ) => {
    return [
      ...notes,
      ...medications,
      ...testResults,
      ...visitSummaries,
      ...procedures,
      ...allergies,
      ...socialHistory,
      ...immunizations,
      ...problems,
      ...vitals,
      ...inbox,
      ...outbox,
      ...myDoctors,
      ...appointments,
      ...getCare,
      ...searchFormattedHelp,
      ...searchFormattedAccount,
      ...searchFormattedBilling
    ];
  }
);
